'use client';

import React from 'react';
import { m } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Shield, Server, Users, Lock, Zap, BarChart, CheckCircle, ArrowRight, Search, PenTool, ShoppingCart, Phone, FileText } from 'lucide-react';
import Link from 'next/link';

const agents = [
    {
        name: 'The Hacker',
        role: 'Lead Generation',
        description: 'Scans millions of social conversations to find high-intent leads actively looking for your solution.',
        icon: Search,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
    },
    {
        name: 'The Editor',
        role: 'Content & SEO',
        description: 'Produces SEO-perfect blogs and LinkedIn thought leadership to build your organic authority.',
        icon: PenTool,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
    },
    {
        name: 'The Buyer',
        role: 'Ad Operations',
        description: 'Manages Meta/Google Ads 24/7—scaling winners and killing losers to protect your budget.',
        icon: ShoppingCart,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
    },
    {
        name: 'The Builder',
        role: 'Landing Pages',
        description: 'Creates high-converting landing pages and lead capture forms in minutes, no code required.',
        icon: FileText,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
    },
    {
        name: 'The Receptionist',
        role: 'Voice AI',
        description: 'Answers every call 24/7 with human-like AI, qualifying leads and booking meetings.',
        icon: Phone,
        color: 'text-pink-600',
        bg: 'bg-pink-50',
    },
];

export default function EnterprisePage() {
    return (
        <div className="min-h-screen bg-white text-neutral-900 font-sans selection:bg-orange-100 selection:text-orange-900">
            <Navbar />

            <main>
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-[#0A0A0A] text-white">
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-b from-orange-500/20 via-purple-500/10 to-transparent blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-t from-blue-500/20 to-transparent blur-3xl" />
                    </div>

                    <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold mb-8"
                        >
                            <span className="flex h-2 w-2 rounded-full bg-orange-500 animate-pulse"></span>
                            Enterprise Solutions
                        </m.div>

                        <m.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-[1.1] tracking-tight max-w-5xl mx-auto"
                        >
                            The AI <span className="text-orange-500">Infrastructure Layer</span> for Enterprise Marketing
                        </m.h1>

                        <m.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-xl sm:text-2xl text-neutral-400 mb-10 leading-relaxed max-w-3xl mx-auto"
                        >
                            Replace fragmented tools and expensive agencies with a unified, autonomous growth engine.
                            5 specialized AI agents working 24/7 to scale your marketing—while you stay in control.
                        </m.p>

                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-4"
                        >
                            <a
                                href="mailto:support@axist.app"
                                className="w-full sm:w-auto px-8 py-4 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-500 transition-all duration-200 shadow-lg hover:shadow-orange-500/25 flex items-center justify-center gap-2"
                            >
                                Contact Enterprise Sales
                                <ArrowRight size={20} />
                            </a>
                            <Link
                                href="/pricing"
                                className="w-full sm:w-auto px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-all duration-200 backdrop-blur-sm flex items-center justify-center gap-2"
                            >
                                View Pricing
                            </Link>
                        </m.div>
                    </div>
                </section>

                {/* The Problem / Solution */}
                <section className="py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <m.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                            >
                                <h2 className="text-4xl font-bold text-neutral-900 mb-6">
                                    Your Marketing Org is Fragmented
                                </h2>
                                <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
                                    You have separate teams—or worse, separate agencies—for SEO, paid acquisition, lead gen,
                                    social listening, and customer support. Each operates in a silo, with its own dashboards,
                                    its own vendors, and its own budget black holes.
                                </p>
                                <p className="text-lg text-neutral-600 leading-relaxed">
                                    <strong className="text-neutral-900">Scaling means hiring more people.</strong> Speed means
                                    burning more money. And despite all that spend, you still have no single source of truth for marketing ROI.
                                </p>
                            </m.div>

                            <m.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl p-8 text-white"
                            >
                                <h3 className="text-2xl font-bold mb-6">Axist Consolidates Everything</h3>
                                <div className="space-y-4">
                                    {[
                                        { label: 'Cost Consolidation', desc: 'Replace 5-10 vendors with one platform. One invoice.' },
                                        { label: 'Speed to Market', desc: 'Launch campaigns in hours, not weeks. No dev tickets.' },
                                        { label: 'Data Unification', desc: 'All marketing signals in one place, feeding one AI.' },
                                        { label: 'Scale Without Headcount', desc: 'AI agents don\'t need PTO. They scale infinitely.' },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <CheckCircle className="text-orange-500 shrink-0 mt-1" size={20} />
                                            <div>
                                                <p className="font-semibold">{item.label}</p>
                                                <p className="text-neutral-400 text-sm">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </m.div>
                        </div>
                    </div>
                </section>

                {/* 5 Agents Section */}
                <section className="py-24 bg-[#FAFAFA]">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <m.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="text-4xl font-bold text-neutral-900 mb-6"
                            >
                                5 Agents. <span className="text-orange-600">One Unified Platform.</span>
                            </m.h2>
                            <m.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="text-xl text-neutral-500"
                            >
                                Each agent is a specialist. Together, they form your complete AI marketing department.
                            </m.p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {agents.map((agent, index) => {
                                const Icon = agent.icon;
                                return (
                                    <m.div
                                        key={agent.name}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="p-8 bg-white rounded-2xl border border-neutral-200 hover:shadow-xl hover:border-neutral-300 transition-all group"
                                    >
                                        <div className={`w-14 h-14 rounded-xl ${agent.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                            <Icon className={agent.color} size={28} />
                                        </div>
                                        <h3 className="text-xl font-bold text-neutral-900 mb-1">{agent.name}</h3>
                                        <p className={`text-sm font-semibold ${agent.color} mb-4 uppercase tracking-wide`}>{agent.role}</p>
                                        <p className="text-neutral-500 text-sm leading-relaxed">{agent.description}</p>
                                    </m.div>
                                );
                            })}

                            {/* Mission Control Card */}
                            <m.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 }}
                                className="p-8 bg-neutral-900 text-white rounded-2xl border border-neutral-700 hover:shadow-xl transition-all group"
                            >
                                <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                    <Shield className="text-orange-500" size={28} />
                                </div>
                                <h3 className="text-xl font-bold mb-1">Mission Control</h3>
                                <p className="text-sm font-semibold text-orange-500 mb-4 uppercase tracking-wide">Human-in-the-Loop</p>
                                <p className="text-neutral-400 text-sm leading-relaxed">All agents report here. You approve high-impact actions with one click. No rogue AI.</p>
                            </m.div>
                        </div>
                    </div>
                </section>

                {/* Enterprise Features */}
                <section className="py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-4xl font-bold text-neutral-900 mb-6">
                                Enterprise-Grade Infrastructure
                            </h2>
                            <p className="text-xl text-neutral-500">
                                Built for scale, security, and compliance.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { icon: Server, title: 'Dedicated Infrastructure', desc: 'Private GPU clusters and vector databases for zero-latency operations.', color: 'text-blue-600', bg: 'bg-blue-50' },
                                { icon: Lock, title: 'Security & Compliance', desc: 'SOC2 compliance, SSO enforcement, and granular RBAC.', color: 'text-purple-600', bg: 'bg-purple-50' },
                                { icon: Zap, title: '<50ms Latency', desc: 'Optimized for high-throughput enterprise environments.', color: 'text-orange-600', bg: 'bg-orange-50' },
                                { icon: Users, title: '24/7 Support', desc: 'Priority support channel with a dedicated account manager.', color: 'text-green-600', bg: 'bg-green-50' },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <m.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.1 }}
                                        className="p-8 bg-neutral-50 rounded-2xl border border-neutral-100 text-center"
                                    >
                                        <div className={`w-16 h-16 mx-auto rounded-2xl ${item.bg} flex items-center justify-center mb-6`}>
                                            <Icon className={item.color} size={32} />
                                        </div>
                                        <h3 className="text-lg font-bold text-neutral-900 mb-2">{item.title}</h3>
                                        <p className="text-sm text-neutral-500">{item.desc}</p>
                                    </m.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-24 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white text-center relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500 rounded-full blur-[128px]" />
                        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-blue-500 rounded-full blur-[128px]" />
                    </div>

                    <div className="max-w-4xl mx-auto px-6 relative z-10">
                        <m.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-4xl md:text-5xl font-bold mb-6"
                        >
                            Ready to build your AI marketing infrastructure?
                        </m.h2>
                        <m.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="text-xl text-neutral-400 mb-10"
                        >
                            Let's discuss how Axist can tackle your most complex challenges.
                        </m.p>
                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="flex flex-col sm:flex-row justify-center gap-4"
                        >
                            <a
                                href="mailto:support@axist.app"
                                className="px-8 py-4 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-500 transition-colors shadow-lg shadow-orange-500/25 flex items-center justify-center gap-2"
                            >
                                Talk to an Expert
                                <ArrowRight size={20} />
                            </a>
                            <Link
                                href="/roadmap"
                                className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-colors backdrop-blur-sm flex items-center justify-center gap-2"
                            >
                                See the Roadmap
                            </Link>
                        </m.div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
